import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Search,
  Star,
  ArrowUpRight,
  GitCompare,
  X,
  Check,
  Plus,
  Globe,
  Monitor,
  HardHat,
  AlertCircle,
  Loader2,
  Tag,
  Building2,
  CalendarDays,
  UserCircle,
  Zap,
  Network,
  ChevronDown,
} from 'lucide-react';
import { fetchTools, fetchDisciplines, fetchToolCategoryMap, fetchToolChildMap, fetchToolPlans } from '../services/aecService';
import {
  fetchProjectStages,
  fetchUserRoles,
  fetchTaskUsecases,
  fetchDisciplineTaskRaw,
  fetchStageTaskRaw,
  fetchRoleTaskRaw,
  getToolsByTask,
} from '../services/aecGraphService';
import { useCompareList } from '../hooks/useCompareList';

// ── Pricing badge styles ─────────────────────────────────────
const PRICING_LABEL = {
  free: 'Free', freemium: 'Freemium', paid: 'One-time',
  subscription: 'Subscription', quote: 'Quote',
};
const PRICING_BADGE = {
  free:         'bg-emerald-50 text-emerald-700 border-emerald-200',
  freemium:     'bg-teal-50 text-teal-700 border-teal-200',
  paid:         'bg-blue-50 text-blue-700 border-blue-200',
  subscription: 'bg-violet-50 text-violet-700 border-violet-200',
  quote:        'bg-amber-50 text-amber-700 border-amber-200',
};

// ── Entity type config ────────────────────────────────────────
const ENTITIES = [
  {
    key: 'disciplines', label: 'Discipline', Icon: Building2,
    pill:    'bg-purple-50  border-purple-200  text-purple-700  hover:bg-purple-100',
    active:  'bg-purple-600 border-purple-600  text-white',
    dot:     'bg-purple-500',
  },
  {
    key: 'stages', label: 'Project Stage', Icon: CalendarDays,
    pill:    'bg-blue-50  border-blue-200  text-blue-700  hover:bg-blue-100',
    active:  'bg-blue-600 border-blue-600  text-white',
    dot:     'bg-blue-500',
  },
  {
    key: 'roles', label: 'Role', Icon: UserCircle,
    pill:    'bg-emerald-50  border-emerald-200  text-emerald-700  hover:bg-emerald-100',
    active:  'bg-emerald-600 border-emerald-600  text-white',
    dot:     'bg-emerald-500',
  },
  {
    key: 'tasks', label: 'Use Case / Task', Icon: Zap,
    pill:    'bg-amber-50  border-amber-200  text-amber-700  hover:bg-amber-100',
    active:  'bg-amber-600 border-amber-600  text-white',
    dot:     'bg-amber-500',
  },
];

// ── Helpers ───────────────────────────────────────────────────
const StarRating = ({ rating, count }) => (
  <div className="flex items-center gap-1.5">
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map((n) => (
        <Star key={n} className={`w-3.5 h-3.5 ${
          n <= Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'
        }`} />
      ))}
    </div>
    <span className="text-xs text-gray-500">
      {Number(rating).toFixed(1)}
      {count != null && <span className="ml-0.5 text-gray-400">({count})</span>}
    </span>
  </div>
);

const ToolCardSkeleton = () => (
  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 flex flex-col gap-4 animate-pulse">
    <div className="flex items-start gap-3">
      <div className="w-11 h-11 rounded-xl bg-gray-100 shrink-0" />
      <div className="flex-1 space-y-2 pt-0.5">
        <div className="h-4 bg-gray-100 rounded w-3/4" />
        <div className="h-3 bg-gray-100 rounded w-1/2" />
      </div>
    </div>
    <div className="flex items-center justify-between">
      <div className="h-3.5 bg-gray-100 rounded w-24" />
      <div className="h-5 bg-gray-100 rounded-full w-20" />
    </div>
    <div className="h-3 bg-gray-100 rounded w-full" />
    <div className="h-3 bg-gray-100 rounded w-4/5" />
    <div className="h-9 bg-gray-100 rounded-xl mt-auto" />
  </div>
);

// ── Tool card ─────────────────────────────────────────────────
const ToolCard = ({ tool, categories, children = [], onCompare, compareList, isChildTool = false, isExpanded = false, onExpand }) => {
  const isSuite      = children.length > 0;
  // Suite cards are "in compare" if any child plan is in the list; standalone tools check their own slug.
  const inCompare    = isSuite
    ? children.some((c) => compareList.includes(c.slug))
    : compareList.includes(tool.slug);
  const compareFull  = compareList.length >= 3;
  const isVibrant    = children.length > 0 || !isChildTool;
  const pricingLabel = PRICING_LABEL[tool.pricing_model] ?? tool.pricing_model;
  const pricingBadge = PRICING_BADGE[tool.pricing_model] ?? 'bg-gray-100 text-gray-600 border-gray-200';

  const handleCompareClick = () => {
    if (!isSuite && (!compareFull || compareList.includes(tool.slug))) {
      onCompare(tool.slug);
    }
  };

  return (
    // Wrapper fills the grid cell so all cards in a row reach the same height
    <div className={`relative h-full ${isSuite && !isExpanded ? 'pb-2' : ''}`}>

      {/* Stacked card layers — only for suite tools while collapsed */}
      {isSuite && !isExpanded && (
        <>
          {/* Deepest layer — widest inset, most translate */}
          <div className="absolute inset-x-5 top-0 bottom-0 rounded-2xl border border-violet-100 bg-white translate-y-2 shadow-sm" />
          {/* Middle layer */}
          <div className="absolute inset-x-2.5 top-0 bottom-0 rounded-2xl border border-violet-200/60 bg-white translate-y-1 shadow-sm" />
        </>
      )}

      {/* Main card — sits above the stack layers, fills wrapper height */}
      <div className={`relative h-full rounded-2xl border transition-all duration-200 flex flex-col overflow-hidden
        ${isVibrant
          ? inCompare
            ? 'bg-white border-violet-400 shadow-lg shadow-violet-100 ring-2 ring-violet-400/20'
            : 'bg-white border-violet-200 shadow-md shadow-violet-100/70 hover:shadow-xl hover:shadow-violet-200/60 hover:-translate-y-0.5'
          : inCompare
            ? 'bg-gray-50 border-blue-300 shadow-md shadow-blue-100 ring-2 ring-blue-300/20'
            : 'bg-gray-50 border-gray-200/80 shadow-none hover:shadow-sm hover:-translate-y-0.5 opacity-85 hover:opacity-100'
        }`}>

        {/* Gradient accent bar — vibrant tools only */}
        {isVibrant && (
          <div className="h-[3px] bg-gradient-to-r from-violet-500 via-indigo-500 to-blue-500 shrink-0" />
        )}

        {/* Compare button — standalone tools only */}
        {!isSuite && (
          <div className="absolute top-3 right-3 z-10">
            <button
              onClick={handleCompareClick}
              disabled={compareFull && !inCompare}
              title={inCompare ? 'Remove from compare' : compareFull ? 'Max 3 tools' : 'Add to compare'}
              className={`w-7 h-7 rounded-full flex items-center justify-center transition-all border
                ${inCompare
                  ? isVibrant
                    ? 'bg-violet-600 border-violet-500 text-white shadow-sm'
                    : 'bg-blue-600 border-blue-500 text-white shadow-sm'
                  : compareFull
                  ? 'bg-gray-100 border-gray-200 text-gray-300 cursor-not-allowed'
                  : isVibrant
                  ? 'bg-white border-violet-200 text-violet-400 hover:border-violet-400 hover:text-violet-600 hover:bg-violet-50'
                  : 'bg-white border-gray-200 text-gray-300 hover:border-gray-400 hover:text-gray-500 hover:bg-gray-100'
                }`}
            >
              {inCompare ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
            </button>
          </div>
        )}

        <div className="p-5 flex flex-col flex-1 gap-4">
          <div className="flex items-start gap-3 pr-8">
            <div className={`w-11 h-11 rounded-xl border flex items-center justify-center shrink-0 overflow-hidden
              ${isVibrant
                ? 'bg-violet-50 border-violet-100'
                : 'bg-gray-100/80 border-gray-200/60'
              }`}>
              {tool.logo_url
                ? <img src={tool.logo_url} alt={tool.name} className="w-full h-full object-contain" />
                : <HardHat className={`w-5 h-5 ${isVibrant ? 'text-violet-400' : 'text-gray-300'}`} />
              }
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <h3 className={`text-sm truncate leading-snug
                  ${isVibrant ? 'font-bold text-gray-900' : 'font-semibold text-gray-700'}`}>
                  {tool.name}
                </h3>
                {isSuite && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-md
                    bg-violet-100 text-violet-700 border border-violet-200 shrink-0">
                    {children.length} plan{children.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <p className={`text-xs mt-0.5 ${isVibrant ? 'text-gray-500' : 'text-gray-400'}`}>
                {tool.vendor_name}
              </p>
              {isSuite && (
                <p className="text-xs text-violet-600/80 mt-0.5 leading-snug font-medium">
                  {children.length <= 3
                    ? children.map((c) => c.name).join(' · ')
                    : `${children.slice(0,2).map((c) => c.name).join(' · ')} +${children.length - 2} more`}
                </p>
              )}
              {categories?.length > 0 && (
                <div className="flex items-center gap-1 mt-1.5">
                  <span className={`inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-md font-medium border
                    ${isVibrant
                      ? 'bg-indigo-50 text-indigo-600 border-indigo-100'
                      : 'bg-gray-100 text-gray-500 border-gray-200'
                    }`}>
                    <Tag className="w-2.5 h-2.5" />{categories[0].name}
                  </span>
                  {categories.length > 1 && (
                    <span className="text-xs text-gray-400 font-medium">+{categories.length - 1}</span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between gap-2">
            {tool.avg_rating > 0
              ? <StarRating rating={tool.avg_rating} count={tool.review_count} />
              : <span className="text-xs text-gray-400">No reviews yet</span>
            }
            <div className="flex items-center gap-1.5 shrink-0">
              {tool.pricing_model && (
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full border
                  ${isVibrant ? pricingBadge : 'bg-gray-100 text-gray-400 border-gray-200'}`}>
                  {pricingLabel}
                </span>
              )}
              {tool.price_from != null && tool.price_from > 0 && (
                <span className="text-xs text-gray-500">
                  from {tool.price_currency ?? 'USD'} {Number(tool.price_from).toLocaleString()}
                </span>
              )}
              {tool.price_from === 0 && (
                <span className="text-xs text-emerald-600 font-medium">Free tier</span>
              )}
            </div>
          </div>

          {tool.tagline && (
            <p className={`text-sm leading-relaxed line-clamp-2
              ${isVibrant ? 'text-gray-600' : 'text-gray-400'}`}>
              {tool.tagline}
            </p>
          )}

          <div className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs
            ${isVibrant ? 'text-gray-400' : 'text-gray-300'}`}>
            {tool.platforms?.includes('web') && (
              <span className="flex items-center gap-1"><Globe className="w-3.5 h-3.5" />Web</span>
            )}
            {(tool.platforms?.includes('windows') || tool.platforms?.includes('mac')) && (
              <span className="flex items-center gap-1"><Monitor className="w-3.5 h-3.5" />Desktop</span>
            )}
            {tool.has_free_trial && (
              <span className="flex items-center gap-1 text-emerald-600">
                <Check className="w-3.5 h-3.5" />Free trial
              </span>
            )}
          </div>

          <div className="mt-auto pt-1">
            {isSuite ? (
              <button
                onClick={() => onExpand(tool.id)}
                className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-sm font-medium transition-colors
                  bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-sm shadow-violet-200"
              >
                {isExpanded
                  ? <>Collapse <ChevronDown className="w-3.5 h-3.5 rotate-180 transition-transform duration-200" /></>
                  : <>View plans <ChevronDown className="w-3.5 h-3.5 transition-transform duration-200" /></>
                }
              </button>
            ) : (
              <Link
                to={`/library/aec/${tool.slug}`}
                className={`w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-sm font-medium transition-colors
                  ${isVibrant
                    ? 'bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-sm shadow-violet-200'
                    : 'bg-gray-200/80 hover:bg-gray-300/80 text-gray-500 hover:text-gray-700'
                  }`}
              >
                View Details <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

// ── Plan card (child tool inside an expanded parent's strip) ──
const PlanCard = ({ plan, isHighlighted = false, compareList = [], onCompare }) => {
  const meta         = plan.tool_meta ?? {};
  const pricingModel = meta.pricing_model ?? null;
  const priceFrom    = meta.price_from    ?? null;
  const priceCurrency = meta.price_currency ?? 'USD';
  const pricingLabel = PRICING_LABEL[pricingModel] ?? pricingModel;
  const pricingBadge = PRICING_BADGE[pricingModel] ?? 'bg-gray-100 text-gray-600 border-gray-200';

  const inCompare   = compareList.includes(plan.slug);
  const compareFull = compareList.length >= 3;

  return (
    <div className={`relative h-full rounded-2xl border bg-white shadow-sm
      hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col overflow-hidden
      ${isHighlighted
        ? 'border-violet-400 ring-2 ring-violet-300 shadow-violet-100'
        : inCompare
        ? 'border-blue-400 ring-2 ring-blue-200'
        : 'border-indigo-200 hover:border-indigo-300'
      }`}>

      <div className="h-[3px] bg-gradient-to-r from-indigo-500 via-blue-500 to-cyan-500 shrink-0" />

      {/* Compare button */}
      {onCompare && (
        <div className="absolute top-3 right-3 z-10">
          <button
            onClick={() => !compareFull || inCompare ? onCompare(plan.slug) : undefined}
            disabled={compareFull && !inCompare}
            title={inCompare ? 'Remove from compare' : compareFull ? 'Max 3 tools' : 'Add to compare'}
            className={`w-7 h-7 rounded-full flex items-center justify-center transition-all border
              ${inCompare
                ? 'bg-blue-600 border-blue-500 text-white shadow-sm'
                : compareFull
                ? 'bg-gray-100 border-gray-200 text-gray-300 cursor-not-allowed'
                : 'bg-white border-indigo-200 text-indigo-400 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50'
              }`}
          >
            {inCompare ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
          </button>
        </div>
      )}

      <div className="p-5 flex flex-col flex-1 gap-4">
        {/* Header */}
        <div className="flex items-start gap-3 pr-8">
          <div className="w-11 h-11 rounded-xl border bg-indigo-50 border-indigo-100 flex items-center justify-center shrink-0">
            <HardHat className="w-5 h-5 text-indigo-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h3 className="text-sm font-bold text-gray-900 leading-snug">{plan.name}</h3>
              {plan.is_primary && (
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md
                  bg-indigo-100 text-indigo-700 border border-indigo-200 shrink-0">
                  Popular
                </span>
              )}
            </div>
            {plan.tool_type && (
              <p className="text-xs text-gray-400 mt-0.5 capitalize">{plan.tool_type}</p>
            )}
          </div>
        </div>

        {/* Pricing */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {pricingModel && (
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${pricingBadge}`}>
              {pricingLabel}
            </span>
          )}
          {priceFrom != null && priceFrom > 0 && (
            <span className="text-xs text-gray-500">
              from {priceCurrency} {Number(priceFrom).toLocaleString()}
            </span>
          )}
          {priceFrom === 0 && (
            <span className="text-xs text-emerald-600 font-medium">Free tier</span>
          )}
          {priceFrom == null && !pricingModel && (
            <span className="text-xs text-gray-400">Pricing on request</span>
          )}
        </div>

        <div className="mt-auto">
          <Link
            to={`/library/aec/${plan.slug}`}
            className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-sm font-medium
              bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700
              text-white shadow-sm shadow-indigo-200 transition-colors"
          >
            View plan <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

// ── Node colours ─────────────────────────────────────────────
const NODE_STYLE = {
  disciplines: {
    ring:    'ring-purple-400',
    bg:      'bg-purple-600',
    light:   'bg-purple-50 border-purple-200',
    text:    'text-purple-700',
    selBg:   'bg-purple-600 border-purple-600 text-white',
    badge:   'bg-purple-600',
    line:    '#c084fc',
    iconBg:  'bg-purple-100 text-purple-600',
  },
  stages: {
    ring:    'ring-blue-400',
    bg:      'bg-blue-600',
    light:   'bg-blue-50 border-blue-200',
    text:    'text-blue-700',
    selBg:   'bg-blue-600 border-blue-600 text-white',
    badge:   'bg-blue-600',
    line:    '#60a5fa',
    iconBg:  'bg-blue-100 text-blue-600',
  },
  roles: {
    ring:    'ring-emerald-400',
    bg:      'bg-emerald-600',
    light:   'bg-emerald-50 border-emerald-200',
    text:    'text-emerald-700',
    selBg:   'bg-emerald-600 border-emerald-600 text-white',
    badge:   'bg-emerald-600',
    line:    '#34d399',
    iconBg:  'bg-emerald-100 text-emerald-600',
  },
  tasks: {
    ring:    'ring-amber-400',
    bg:      'bg-amber-500',
    light:   'bg-amber-50 border-amber-200',
    text:    'text-amber-700',
    selBg:   'bg-amber-500 border-amber-500 text-white',
    badge:   'bg-amber-500',
    line:    '#fbbf24',
    iconBg:  'bg-amber-100 text-amber-600',
  },
};

// ── Graph Node Diagram ────────────────────────────────────────
const GraphNodeDiagram = ({ entityData, entityGroups, selections, onToggle, onClear, matchedCount, graphLoading }) => {
  const [openNode, setOpenNode] = useState(null);
  const totalSelected = Object.values(selections).flat().length;

  const toggleNode = (key) => setOpenNode((prev) => (prev === key ? null : key));

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">

      {/* ── Header ── */}
      <div className="px-5 pt-4 pb-3 border-b border-gray-100 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gray-900 flex items-center justify-center shrink-0">
            <Network className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">Discover by Context</p>
            <p className="text-xs text-gray-400">Click a node to expand and select options</p>
          </div>
        </div>
        {totalSelected > 0 && (
          <div className="flex items-center gap-2.5 shrink-0">
            {graphLoading
              ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
              : <span className="text-xs font-semibold text-gray-900 bg-gray-100 px-2.5 py-1 rounded-full">
                  {matchedCount} tool{matchedCount !== 1 ? 's' : ''} matched
                </span>
            }
            <button onClick={onClear}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-700 transition-colors">
              <X className="w-3.5 h-3.5" />Clear
            </button>
          </div>
        )}
      </div>

      {/* ── Node map ── */}
      <div className="px-6 pt-6 pb-4">
        <div className="relative flex items-start justify-between">

          {/* SVG connection lines — drawn behind the nodes */}
          <svg className="absolute inset-0 w-full pointer-events-none" style={{ height: 52, top: 0 }}
            preserveAspectRatio="none">
            {/* lines between node centres: 12.5% → 37.5% → 62.5% → 87.5% */}
            {[
              { x1: '12.5%', x2: '37.5%', key: 'disciplines-stages',  c1: NODE_STYLE.disciplines.line, c2: NODE_STYLE.stages.line },
              { x1: '37.5%', x2: '62.5%', key: 'stages-roles',        c1: NODE_STYLE.stages.line,      c2: NODE_STYLE.roles.line  },
              { x1: '62.5%', x2: '87.5%', key: 'roles-tasks',         c1: NODE_STYLE.roles.line,       c2: NODE_STYLE.tasks.line  },
            ].map(({ x1, x2, key, c1, c2 }) => (
              <g key={key}>
                <defs>
                  <linearGradient id={`grad-${key}`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%"   stopColor={c1} stopOpacity="0.6" />
                    <stop offset="100%" stopColor={c2} stopOpacity="0.6" />
                  </linearGradient>
                </defs>
                <line x1={x1} y1="26" x2={x2} y2="26"
                  stroke={`url(#grad-${key})`} strokeWidth="2" strokeDasharray="4 3" />
              </g>
            ))}
          </svg>

          {/* 4 nodes */}
          {ENTITIES.map(({ key, label, Icon }) => {
            const style    = NODE_STYLE[key];
            const items    = entityData[key] ?? [];
            const picked   = selections[key] ?? [];
            const isOpen   = openNode === key;
            const hasItems = items.length > 0;
            const hasSel   = picked.length > 0;

            return (
              <div key={key} className="relative z-10 flex flex-col items-center flex-1">

                {/* Main node circle */}
                <button
                  onClick={() => hasItems && toggleNode(key)}
                  disabled={!hasItems}
                  className={`relative w-[52px] h-[52px] rounded-full border-2 flex items-center justify-center
                    transition-all duration-200 shadow-sm
                    ${hasSel
                      ? `${style.bg} border-transparent text-white shadow-md ring-4 ${style.ring} ring-opacity-30`
                      : isOpen
                      ? `bg-white border-gray-400 ${style.text} shadow-md`
                      : hasItems
                      ? `bg-white border-gray-200 text-gray-400 hover:border-gray-400 hover:${style.text}`
                      : 'bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed'
                    }`}
                >
                  <Icon className="w-5 h-5" />
                  {/* Selection count badge */}
                  {hasSel && (
                    <span className={`absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1
                      rounded-full ${style.badge} text-white text-[10px] font-bold
                      flex items-center justify-center shadow`}>
                      {picked.length}
                    </span>
                  )}
                  {/* Open indicator dot */}
                  {isOpen && !hasSel && (
                    <span className={`absolute -bottom-1 left-1/2 -translate-x-1/2
                      w-1.5 h-1.5 rounded-full ${style.bg}`} />
                  )}
                </button>

                {/* Label */}
                <p className={`text-xs font-medium mt-2 text-center leading-tight max-w-[80px]
                  ${hasSel ? 'text-gray-900' : 'text-gray-500'}`}>
                  {label}
                  {hasSel && (
                    <span className={`block text-[10px] font-semibold ${style.text}`}>
                      {picked.length} selected
                    </span>
                  )}
                </p>
              </div>
            );
          })}
        </div>

        {/* ── Expanded node panel ── */}
        {openNode && (() => {
          const entity = ENTITIES.find((e) => e.key === openNode);
          const style  = NODE_STYLE[openNode];
          const picked = selections[openNode] ?? [];
          // Use grouped data when available, fall back to flat list in one group
          const groups = entityGroups[openNode]?.length
            ? entityGroups[openNode]
            : [{ label: null, items: entityData[openNode] ?? [] }];

          const PillButton = ({ item }) => {
            const isSel = picked.includes(item.id);
            return (
              <button
                key={item.id}
                onClick={() => onToggle(openNode, item.id)}
                className={`text-xs px-1 py-0.5 rounded-full border font-medium transition-all
                  ${isSel ? style.selBg : `bg-gray border-gray-200 ${style.text} hover:border-gray-400`}`}
              >
                {isSel && <Check className="inline w-3 h-3 mr-1 opacity-80" />}
                {item.name}
              </button>
            );
          };

          return (
            <div className={`mt-5 rounded-xl border p-4 ${style.light} transition-all`}>
              {/* Panel header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className={`w-6 h-6 rounded-md flex items-center justify-center ${style.iconBg}`}>
                    <entity.Icon className="w-3.5 h-3.5" />
                  </span>
                  <p className={`text-xs font-semibold ${style.text}`}>{entity.label}</p>
                </div>
                {picked.length > 0 && (
                  <button
                    onClick={() => picked.forEach((id) => onToggle(openNode, id))}
                    className="text-xs text-gray-400 hover:text-gray-700 transition-colors"
                  >
                    Clear {entity.label}
                  </button>
                )}
              </div>

              {/* Grouped cards — horizontal rows, scroll after 2 rows */}
              <div className="flex flex-wrap gap-2.5 overflow-y-auto overflow-x-hidden"
                style={{ maxHeight: '15rem' }}>
                {groups.map((group, gi) => (
                  <div key={gi}
                    className="bg-white/70 rounded-xl border border-white shadow-sm px-3 py-2.5
                      flex flex-col gap-2 min-w-[150px] flex-1 basis-[150px]">
                    {group.label && (
                      <p className={`text-[10px] font-semibold uppercase tracking-wider leading-none ${style.text} opacity-70`}>
                        {group.label}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-0.5">
                      {group.items.map((item) => <PillButton key={item.id} item={item} />)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Empty state */}
        {ENTITIES.every(({ key }) => (entityData[key] ?? []).length === 0) && (
          <p className="text-sm text-gray-400 text-center py-4">
            Seed discipline, project_stage, user_role and task_usecase tables to enable graph discovery.
          </p>
        )}
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────
const AECLibrary = () => {
  const navigate = useNavigate();

  // ── Tool data ──
  const [allTools, setAllTools]     = useState([]);   // full unfiltered list
  const [tools, setTools]           = useState([]);   // displayed (filtered) list
  const [categoryMap, setCategoryMap] = useState({});
  const [childMap, setChildMap]       = useState({});
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);

  // ── Graph entity data ──
  const [entityData, setEntityData] = useState({
    disciplines: [], stages: [], roles: [], tasks: [],
  });
  // ── Grouped data for the expanded panels ──
  const [entityGroups, setEntityGroups] = useState({
    disciplines: [], stages: [], roles: [], tasks: [],
  });

  // ── Graph selections ──
  const [selections, setSelections] = useState({
    disciplines: [], stages: [], roles: [], tasks: [],
  });
  const [graphLoading, setGraphLoading] = useState(false);
  const [graphIds, setGraphIds]         = useState(null); // null = no filter active

  // ── Search ──
  const [searchTerm, setSearchTerm] = useState('');
  // ── Compare ──
  const { compareList, toggleCompare, clearCompare } = useCompareList();

  // ── Expansion state ──
  const [expandedId, setExpandedId]             = useState(null);
  const [planDetailsMap, setPlanDetailsMap]     = useState({});
  const planFetchedRef                          = useRef(new Set());
  const [highlightedChildIds, setHighlightedChildIds] = useState(new Set());
  const [junctionMapsLoaded, setJunctionMapsLoaded] = useState(false);
  const pendingRestoreRef = useRef(null);
  const hasMountedRef     = useRef(false);

  // ── Junction maps: entity_uuid → [task_uuid, ...] ──
  // Keys and values are UUIDs — slug-independent, always reliable.
  const junctionMaps    = useRef({ disciplines: {}, stages: {}, roles: {} });
  // task_uuid → slug — used only at RPC call boundary
  const taskIdToSlugRef = useRef({});

  // ── Load entity data + junction maps for graph panel ──
  useEffect(() => {
    Promise.allSettled([
      fetchDisciplines(),
      fetchProjectStages(),
      fetchUserRoles(),
      fetchTaskUsecases(),
      fetchDisciplineTaskRaw(),
      fetchStageTaskRaw(),
      fetchRoleTaskRaw(),
    ]).then(([disc, stages, roles, tasks, dtRaw, stRaw, trRaw]) => {
      const disciplines = disc.status   === 'fulfilled' ? disc.value   : [];
      const stagesArr   = stages.status === 'fulfilled' ? stages.value : [];
      const rolesArr    = roles.status  === 'fulfilled' ? roles.value  : [];
      const tasksArr    = tasks.status  === 'fulfilled' ? tasks.value  : [];

      setEntityData({ disciplines, stages: stagesArr, roles: rolesArr, tasks: tasksArr });

      // task uuid → slug (only needed at RPC call boundary)
      taskIdToSlugRef.current = Object.fromEntries(
        tasksArr.filter((t) => t.slug).map((t) => [t.id, t.slug])
      );

      // Junction maps: entity_uuid → [task_uuid, ...]  (no slug dependency)
      const disciplinesMap = {};
      for (const row of (dtRaw.status === 'fulfilled' ? dtRaw.value : [])) {
        if (row.discipline_id && row.task_id)
          (disciplinesMap[row.discipline_id] = disciplinesMap[row.discipline_id] || []).push(row.task_id);
      }

      const stagesMap = {};
      for (const row of (stRaw.status === 'fulfilled' ? stRaw.value : [])) {
        if (row.stage_id && row.task_id)
          (stagesMap[row.stage_id] = stagesMap[row.stage_id] || []).push(row.task_id);
      }

      const rolesMap = {};
      for (const row of (trRaw.status === 'fulfilled' ? trRaw.value : [])) {
        if (row.role_id && row.task_id)
          (rolesMap[row.role_id] = rolesMap[row.role_id] || []).push(row.task_id);
      }

      junctionMaps.current = { disciplines: disciplinesMap, stages: stagesMap, roles: rolesMap };

      // ── Compute entity groups ─────────────────────────────────

      // Disciplines: 4 equal chunks, no labels
      const discChunkSize = Math.ceil(disciplines.length / 4);
      const discGroups = [];
      for (let i = 0; i < disciplines.length; i += discChunkSize) {
        const chunk = disciplines.slice(i, i + discChunkSize);
        if (chunk.length) discGroups.push({ label: null, items: chunk });
      }

      // Project stages: grouped into 4 natural phases by sort_order
      const stagesSorted = [...stagesArr].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      const STAGE_PHASES = [
        { label: 'Pre-Design',            min: 1, max: 1 },
        { label: 'Design',                min: 2, max: 4 },
        { label: 'Procurement & Build',   min: 5, max: 7 },
        { label: 'Handover & Operations', min: 8, max: 99 },
      ];
      const stageGroups = STAGE_PHASES
        .map(({ label, min, max }) => ({
          label,
          items: stagesSorted.filter((s) => (s.sort_order || 0) >= min && (s.sort_order || 0) <= max),
        }))
        .filter((g) => g.items.length > 0);

      // Roles: grouped by discipline domain (hardcoded by role name)
      const ROLE_GROUP_DEFS = [
        { label: 'Architecture & Planning',  names: ['Architect', 'Architectural Drafter', 'Urban Planner', 'Landscape Architect'] },
        { label: 'Engineering',              names: ['Structural Engineer', 'Civil Engineer', 'MEP Engineer', 'M&E Engineer', 'Geotechnical Engineer', 'Environmental Engineer'] },
        { label: 'BIM & Digital',            names: ['BIM Coordinator', 'BIM Manager', 'BIM Modeller'] },
        { label: 'Project & Site',           names: ['Project Manager', 'Construction Manager', 'Contractor', 'Site Engineer', 'Site Supervisor'] },
        { label: 'Commercial',               names: ['Quantity Surveyor', 'Cost Engineer'] },
        { label: 'Sustainability & FM',      names: ['Sustainability Consultant', 'Facilities Manager', 'Asset Manager', 'Owner / Developer'] },
      ];
      const roleNameToGroup = {};
      ROLE_GROUP_DEFS.forEach(({ label, names }) => names.forEach((n) => (roleNameToGroup[n] = label)));
      const rolesBucket = {};
      rolesArr.forEach((r) => {
        const g = roleNameToGroup[r.name] || 'Other';
        (rolesBucket[g] = rolesBucket[g] || []).push(r);
      });
      const roleGroups = [
        ...ROLE_GROUP_DEFS.map(({ label }) => ({ label, items: rolesBucket[label] || [] })).filter((g) => g.items.length),
        ...(rolesBucket['Other']?.length ? [{ label: 'Other', items: rolesBucket['Other'] }] : []),
      ];

      // Tasks: grouped by primary discipline (using disciplinesMap for task→disc mapping)
      const taskDiscName = {};
      disciplines.forEach((d) => {
        (disciplinesMap[d.id] || []).forEach((taskId) => {
          if (!taskDiscName[taskId]) taskDiscName[taskId] = d.name;
        });
      });
      const taskBucket = {};
      tasksArr.forEach((t) => {
        const g = taskDiscName[t.id] || 'Other';
        (taskBucket[g] = taskBucket[g] || []).push(t);
      });
      const taskGroups = [
        ...disciplines.filter((d) => taskBucket[d.name]).map((d) => ({ label: d.name, items: taskBucket[d.name] })),
        ...(taskBucket['Other']?.length ? [{ label: 'Other', items: taskBucket['Other'] }] : []),
      ];

      setEntityGroups({
        disciplines: discGroups,
        stages:      stageGroups,
        roles:       roleGroups,
        tasks:       taskGroups,
      });

      // ── DEBUG: remove once confirmed working ──
      console.log('[graph] junction maps loaded (UUID-keyed)', {
        discipline_entries: Object.keys(disciplinesMap).length,
        stage_entries:      Object.keys(stagesMap).length,
        role_entries:       Object.keys(rolesMap).length,
        task_slugs_loaded:  Object.keys(taskIdToSlugRef.current).length,
        rawCounts: {
          discipline_task: dtRaw.status === 'fulfilled' ? dtRaw.value.length : 'FAILED: ' + dtRaw.reason,
          stage_task:      stRaw.status === 'fulfilled' ? stRaw.value.length : 'FAILED: ' + stRaw.reason,
          task_role:       trRaw.status === 'fulfilled' ? trRaw.value.length : 'FAILED: ' + trRaw.reason,
        },
      });

      setJunctionMapsLoaded(true);
    });
  }, []);

  // ── Load all tools once ──
  const loadAllTools = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTools();
      setAllTools(data);
      if (data.length > 0) {
        const ids = data.map((t) => t.id);
        const [catMap, cMap] = await Promise.all([
          fetchToolCategoryMap(ids),
          fetchToolChildMap(ids),
        ]);
        setCategoryMap(catMap);
        setChildMap(cMap);
      }
    } catch (err) {
      console.error('AEC Library fetch error:', err);
      setError('Failed to load tools. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAllTools(); }, [loadAllTools]);

  // ── Compute intersected task UUIDs from current selections ──
  // selections store UUIDs; junction maps are UUID → [task UUIDs].
  // Within each active dimension: union of that dimension's selected items' tasks.
  // Across dimensions: intersection — task must appear in ALL active dimensions.
  const computeTaskIds = useCallback((sel) => {
    const maps = junctionMaps.current;
    const activeDimSets = [];

    if (sel.disciplines.length) {
      const s = new Set();
      sel.disciplines.forEach((id) => (maps.disciplines[id] || []).forEach((t) => s.add(t)));
      activeDimSets.push(s);
    }
    if (sel.stages.length) {
      const s = new Set();
      sel.stages.forEach((id) => (maps.stages[id] || []).forEach((t) => s.add(t)));
      activeDimSets.push(s);
    }
    if (sel.roles.length) {
      const s = new Set();
      sel.roles.forEach((id) => (maps.roles[id] || []).forEach((t) => s.add(t)));
      activeDimSets.push(s);
    }

    if (activeDimSets.length === 0) return sel.tasks; // only manual task picks (already UUIDs)

    // Intersect across active dimensions
    const [first, ...rest] = activeDimSets;
    const intersected = [...first].filter((t) => rest.every((s) => s.has(t)));

    // Add any manually-selected tasks on top
    const result = new Set(intersected);
    sel.tasks.forEach((t) => result.add(t));
    return [...result];
  }, []);

  // ── Graph query: tasks drive tool results ──
  // computeTaskIds resolves discipline/stage/role selections → task UUIDs.
  // Task UUIDs are translated to slugs via taskIdToSlugRef before the RPC call.
  const runGraphQuery = useCallback(async (sel) => {
    const taskIds = computeTaskIds(sel);
    const hasAnySelection = Object.values(sel).flat().length > 0;

    if (!hasAnySelection) { setGraphIds(null); return; }

    // ── DEBUG: remove once confirmed working ──
    console.log('[graph] runGraphQuery', { sel, taskIds, maps: junctionMaps.current });

    setGraphLoading(true);
    try {
      const idSets = [];
      for (const taskId of taskIds) {
        const slug = taskIdToSlugRef.current[taskId];
        if (!slug) { console.warn('[graph] no slug for task id', taskId); continue; }
        try {
          const res = await getToolsByTask(slug);
          console.log('[graph] getToolsByTask', slug, '→', res.length, 'tools');
          idSets.push(res.map((r) => r.tool_id));
        } catch (e) {
          console.warn('[graph] getToolsByTask failed', slug, e);
        }
      }
      const union = new Set(idSets.flat());
      console.log('[graph] final tool IDs count:', union.size);
      setGraphIds(union.size > 0 ? union : new Set());
    } catch (err) {
      console.error('Graph query error:', err);
      setGraphIds(null);
    } finally {
      setGraphLoading(false);
    }
  }, [computeTaskIds]);

  // ── Apply search + graph filters to allTools ──
  useEffect(() => {
    const newHighlightedChildIds = new Set();
    let filtered = allTools;

    // Search filter (client-side on already-fetched list)
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter((t) => {
        if (
          t.name?.toLowerCase().includes(q) ||
          t.tagline?.toLowerCase().includes(q) ||
          t.vendor_name?.toLowerCase().includes(q)
        ) return true;

        // Check if any child of this parent matches the search
        const children = childMap[t.id] ?? [];
        const matchedChildren = children.filter(
          (c) =>
            c.name?.toLowerCase().includes(q) ||
            c.slug?.toLowerCase().includes(q)
        );
        matchedChildren.forEach((c) => newHighlightedChildIds.add(c.id));
        return matchedChildren.length > 0;
      });
    }

    // Graph filter
    if (graphIds !== null) {
      filtered = filtered.filter((t) => {
        if (graphIds.has(t.id)) return true;

        // Check if any child of this parent is in graphIds
        const children = childMap[t.id] ?? [];
        const matchedChildren = children.filter((c) => graphIds.has(c.id));
        matchedChildren.forEach((c) => newHighlightedChildIds.add(c.id));
        return matchedChildren.length > 0;
      });
    }

    setHighlightedChildIds(newHighlightedChildIds);
    setTools(filtered);
  }, [allTools, searchTerm, graphIds, childMap]);

  // ── Toggle a graph selection ──
  // When discipline/stage/role changes, recompute auto-selected tasks.
  // Task panel picks are preserved as manual additions on top.
  const handleToggle = useCallback((entityKey, identifier) => {
    setSelections((prev) => {
      const current = prev[entityKey];
      const next = current.includes(identifier)
        ? current.filter((s) => s !== identifier)
        : [...current, identifier];
      const updated = { ...prev, [entityKey]: next };

      // Recompute tasks for all dimension changes (not when toggling tasks directly)
      if (entityKey !== 'tasks') {
        updated.tasks = computeTaskIds({ ...updated, tasks: [] }); // auto only, no manual carry-over on dim change
      }

      runGraphQuery(updated);
      return updated;
    });
  }, [runGraphQuery]);

  const handleClearGraph = useCallback(() => {
    const cleared = { disciplines: [], stages: [], roles: [], tasks: [] };
    setSelections(cleared);
    setGraphIds(null);
  }, []);

  // ── Child tool id set (for isChildTool prop) ──
  const childToolIds = useMemo(() => {
    const ids = new Set();
    Object.values(childMap).forEach((arr) => arr.forEach((c) => ids.add(c.id)));
    return ids;
  }, [childMap]);

  const handleExpand = useCallback((toolId) => {
    setExpandedId((prev) => (prev === toolId ? null : toolId));
    if (!planFetchedRef.current.has(toolId)) {
      planFetchedRef.current.add(toolId);
      fetchToolPlans(toolId)
        .then((plans) => setPlanDetailsMap((prev) => ({ ...prev, [toolId]: plans })))
        .catch(console.error);
    }
  }, []);

  // ── Auto-fetch plans for parents whose children are highlighted ──
  useEffect(() => {
    if (highlightedChildIds.size === 0) return;
    Object.entries(childMap).forEach(([parentId, children]) => {
      const hasHighlighted = children.some((c) => highlightedChildIds.has(c.id));
      if (hasHighlighted && !planFetchedRef.current.has(parentId)) {
        planFetchedRef.current.add(parentId);
        fetchToolPlans(parentId)
          .then((plans) => setPlanDetailsMap((prev) => ({ ...prev, [parentId]: plans })))
          .catch(console.error);
      }
    });
  }, [highlightedChildIds, childMap]);

  // ── sessionStorage: restore on mount ──
  const STATE_KEY = 'aec-library:state';
  useEffect(() => {
    const saved = sessionStorage.getItem(STATE_KEY);
    if (!saved) return;
    try {
      const { searchTerm: q, selections: sel } = JSON.parse(saved);
      if (q) setSearchTerm(q);
      if (sel && Object.values(sel).flat().length > 0) {
        setSelections(sel);
        pendingRestoreRef.current = sel;
      }
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── sessionStorage: save on every change after first render ──
  useEffect(() => {
    if (!hasMountedRef.current) { hasMountedRef.current = true; return; }
    sessionStorage.setItem(STATE_KEY, JSON.stringify({ searchTerm, selections }));
  }, [searchTerm, selections]);

  // ── sessionStorage: run pending graph restore once junction maps are ready ──
  useEffect(() => {
    if (!junctionMapsLoaded || !pendingRestoreRef.current) return;
    runGraphQuery(pendingRestoreRef.current);
    pendingRestoreRef.current = null;
  }, [junctionMapsLoaded, runGraphQuery]);

  const handleGoCompare = () => {
    if (compareList.length >= 2)
      navigate(`/library/aec/compare/${compareList.join('-vs-')}`);
  };

  const totalSelected   = Object.values(selections).flat().length;
  const hasSearch       = searchTerm !== '';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">

      {/* ── Action bar ── */}
      <div className="sticky top-[123px] z-[10] bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center gap-2 flex-wrap">

          {/* Action buttons — left */}
          <button
            onClick={() => navigate(
              compareList.length >= 2
                ? `/library/aec/compare/${compareList.join('-vs-')}`
                : '/library/aec/compare'
            )}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              compareList.length >= 2
                ? 'bg-gray-900 text-white hover:bg-gray-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <GitCompare className="w-3.5 h-3.5" />
            Compare
            {compareList.length > 0 && (
              <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
                compareList.length >= 2 ? 'bg-white/20 text-white' : 'bg-gray-300 text-gray-700'
              }`}>
                {compareList.length}
              </span>
            )}
          </button>
          <Link to="/resources" className="px-3 py-1.5 text-sm font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 no-underline transition-colors">
            Guide
          </Link>
          <Link to="/resources" className="px-3 py-1.5 text-sm font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 no-underline transition-colors">
            Resources
          </Link>

          {/* Search — right */}
          <div className="relative ml-auto w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search tools, vendors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-8 py-1.5 text-sm rounded-lg bg-gray-100 border border-transparent focus:outline-none focus:ring-2 focus:ring-purple-400 focus:bg-white focus:border-purple-300 text-gray-900 placeholder-gray-400 transition-all"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 space-y-6">

        {/* Graph discovery panel */}
        <GraphNodeDiagram
          entityData={entityData}
          entityGroups={entityGroups}
          selections={selections}
          onToggle={handleToggle}
          onClear={handleClearGraph}
          matchedCount={graphIds !== null ? tools.length : allTools.length}
          graphLoading={graphLoading}
        />

        {/* Result bar */}
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-gray-500">
            {loading ? (
              <span className="flex items-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />Loading…
              </span>
            ) : (
              <>
                <span className="font-semibold text-gray-900">{tools.length}</span>
                {' '}tool{tools.length !== 1 ? 's' : ''}
                {(totalSelected > 0 || hasSearch) && (
                  <span className="text-gray-400 ml-1">
                    {totalSelected > 0 && ` · ${totalSelected} context selection${totalSelected > 1 ? 's' : ''}`}
                  </span>
                )}
              </>
            )}
          </p>
          {(totalSelected > 0 || hasSearch) && (
            <button
              onClick={() => { handleClearGraph(); setSearchTerm(''); }}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-700 transition-colors"
            >
              <X className="w-3 h-3" />Clear all
            </button>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
            <button onClick={loadAllTools} className="ml-auto text-red-600 hover:text-red-800 underline">
              Retry
            </button>
          </div>
        )}

        {/* Skeletons */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => <ToolCardSkeleton key={i} />)}
          </div>
        )}

        {/* Graph loading overlay on grid */}
        {!loading && graphLoading && (
          <div className="flex items-center justify-center gap-2 py-4 text-sm text-gray-400">
            <Loader2 className="w-4 h-4 animate-spin" />
            Traversing knowledge graph…
          </div>
        )}

        {/* Tool grid — child tools are hidden; they appear in a full-width strip below their parent */}
        {!loading && !error && tools.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {tools.filter((tool) => !childToolIds.has(tool.id)).flatMap((tool) => {
              const toolChildren        = childMap[tool.id] ?? [];
              const hasHighlightedChild = toolChildren.some((c) => highlightedChildIds.has(c.id));
              const isExpanded          = (expandedId === tool.id || hasHighlightedChild) && toolChildren.length > 0;
              const plans               = planDetailsMap[tool.id];

              const items = [
                <ToolCard
                  key={tool.id}
                  tool={tool}
                  categories={categoryMap[tool.id] ?? []}
                  children={toolChildren}
                  onCompare={toggleCompare}
                  compareList={compareList}
                  isChildTool={false}
                  isExpanded={isExpanded}
                  onExpand={handleExpand}
                />,
              ];

              if (isExpanded) {
                items.push(
                  <div key={`${tool.id}-plans`} className="col-span-full">
                    <div className="rounded-2xl border border-violet-200 bg-violet-50/40 p-4">
                      <div className="flex items-center justify-between mb-4">
                        <p className="text-sm font-semibold text-violet-900">
                          {tool.name} — Plans
                        </p>
                        <Link
                          to={`/library/aec/${tool.slug}`}
                          className="text-xs font-medium text-violet-600 hover:text-violet-800 flex items-center gap-0.5 transition-colors"
                        >
                          View all <ArrowUpRight className="w-3 h-3" />
                        </Link>
                      </div>
                      {plans ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                          {plans.map((plan) => (
                            <PlanCard
                              key={plan.id}
                              plan={plan}
                              isHighlighted={highlightedChildIds.has(plan.id)}
                              compareList={compareList}
                              onCompare={toggleCompare}
                            />
                          ))}
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                          {toolChildren.map((c) => <ToolCardSkeleton key={c.id} />)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              }

              return items;
            })}
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && tools.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
              {totalSelected > 0
                ? <Network className="w-7 h-7 text-gray-400" />
                : <HardHat className="w-7 h-7 text-gray-400" />
              }
            </div>
            <p className="text-base font-semibold text-gray-900 mb-1">
              {totalSelected > 0 || hasSearch
                ? 'No tools matched your context'
                : 'No tools yet'
              }
            </p>
            <p className="text-sm text-gray-400 mb-5 max-w-sm">
              {totalSelected > 0
                ? 'The knowledge graph has no tool links for this combination yet. Add data via sync_graph.py or manual task_tool inserts.'
                : hasSearch
                ? 'Try a different search term.'
                : 'Tools are added by admins via Supabase Studio.'
              }
            </p>
            {(totalSelected > 0 || hasSearch) && (
              <button
                onClick={() => { handleClearGraph(); setSearchTerm(''); }}
                className="px-4 py-2 rounded-xl bg-gray-900 hover:bg-gray-700 text-white text-sm font-medium transition-colors"
              >
                Clear selections
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Floating compare bar ── */}
      {compareList.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 w-full max-w-md px-4">
          <div className="flex items-center justify-between gap-3 px-5 py-3 rounded-2xl bg-gray-900 shadow-xl shadow-black/20 border border-white/10">
            <p className="text-sm text-white">
              <span className="font-semibold">{compareList.length}</span>
              <span className="text-gray-400"> / 3 selected</span>
              {compareList.length < 2 && <span className="text-gray-500 ml-1.5">— pick at least 2</span>}
            </p>
            <div className="flex items-center gap-2 shrink-0">
              {compareList.length >= 2 && (
                <button
                  onClick={handleGoCompare}
                  className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-white hover:bg-gray-100 text-gray-900 text-sm font-semibold transition-colors"
                >
                  <GitCompare className="w-4 h-4" />Compare
                </button>
              )}
              <button onClick={clearCompare} className="text-gray-500 hover:text-white transition-colors p-1">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AECLibrary;
